import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ApiUrl from '../../utils/config';
import { AgeCategoryInTournament, ApproveBracketRequest, BracketPreviewModel, MatchApproveDto, Participant, ParticipantClient, RoundApproveDto, Tournament, TournamentClient, TournamentOfficialsModel, WeightCategoryInTournament } from '../../utils/apiClient';
import axios from 'axios';
import { useAuth } from '../Auth/AuthContext';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { formatShortName } from '../../utils/personName';
import { formatAgeCategoryLabel, formatWeightCategoryLabel } from '../../utils/categoryResolver';
import './TournamentDetail.scss';
import { AdminParticipantModal } from './AdminParticipantModal';
import BracketsPreview from '../TournamentList/BracketsPreview';
import CreatePersonModal from './CreatePersonModal';
import EditParticipantModal from './EditParticipantModal';
import WeighInModal from './WeighInModal';
import CategoryDrawPanel from './CategoryDrawPanel';
import CategoryOverviewGrid from './CategoryOverviewGrid';
import {
  buildApproveRequest,
  CategoryDrawModel,
  CompetitionFormat,
  fetchCategoryDraw,
  fetchPreviewBracket,
} from '../../utils/categoryDrawApi';
import { fetchCategoryOverviews, TournamentCategoryOverview } from '../../utils/categoryOverviewApi';
import { WeighInProtocolStatus, weighInProtocolClient } from '../../utils/weighInProtocolApi';
import { bracketPrintClient } from '../../utils/bracketPrintApi';
import { tournamentExportClient } from '../../utils/tournamentExportApi';
import { finishTournament, reopenTournament } from '../../utils/tournamentStatusApi';
import { isRegistrationOpen } from '../../utils/participantApplyApi';
import { REPRESENTATIVE_ROLE } from '../../utils/guardianApi';
import { getTournamentLifecycle, tournamentLifecycleLabel } from '../../utils/tournamentLifecycle';

export const TournamentDetail = () => {
  const appContext = React.useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const [tournament, setTournament] = useState<Tournament | undefined>(undefined);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  
  // State для выбора категорий и предпросмотра сетки
  const [selectedAgeCategoryForBracket, setSelectedAgeCategoryForBracket] = useState<AgeCategoryInTournament | null>(null);
  const [selectedWeightCategoryForBracket, setSelectedWeightCategoryForBracket] = useState<WeightCategoryInTournament | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [bracketPreview, setBracketPreview] = useState<BracketPreviewModel | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isAdminRegisterModalOpen, setIsAdminRegisterModalOpen] = useState(false);
  const [isCreatePersonModalOpen, setIsCreatePersonModalOpen] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);
  const [isWeighInModalOpen, setIsWeighInModalOpen] = useState(false);
  const [activeWeighInStatus, setActiveWeighInStatus] = useState<WeighInProtocolStatus | null>(null);
  const [weighInStatuses, setWeighInStatuses] = useState<WeighInProtocolStatus[]>([]);
  const [loadingWeighInStatuses, setLoadingWeighInStatuses] = useState(false);
  const [printingWeighInKey, setPrintingWeighInKey] = useState<string | null>(null);
  const [printingCompetitionKey, setPrintingCompetitionKey] = useState<string | null>(null);
  const [categoryDraw, setCategoryDraw] = useState<CategoryDrawModel | null>(null);
  const [loadingDraw, setLoadingDraw] = useState(false);
  const [printingProtocol, setPrintingProtocol] = useState(false);
  const [exportingAllProtocols, setExportingAllProtocols] = useState(false);
  const [deletingTournament, setDeletingTournament] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [chiefJudgeName, setChiefJudgeName] = useState('');
  const [chiefSecretaryName, setChiefSecretaryName] = useState('');
  const [weighInProtocolTitle, setWeighInProtocolTitle] = useState('');
  const [competitionProtocolTitle, setCompetitionProtocolTitle] = useState('');
  const [savingOfficials, setSavingOfficials] = useState(false);
  const [categoryOverviews, setCategoryOverviews] = useState<TournamentCategoryOverview[]>([]);
  const [loadingOverviews, setLoadingOverviews] = useState(true);
  const [isFormatOverridden, setIsFormatOverridden] = useState(false);
  const [manualFormat, setManualFormat] = useState<CompetitionFormat>(CompetitionFormat.Olympic);
  const [rebuildingCategoryKey, setRebuildingCategoryKey] = useState<string | null>(null);
  const categoriesSectionRef = React.useRef<HTMLDivElement>(null);
  const overviewSectionRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const tournamentClient = new TournamentClient(ApiUrl, axios);
  const participantClient = new ParticipantClient(ApiUrl, axios);
  const { user, isAdmin, isOwner } = useAuth();
  // Родитель без роли «Спортсмен» подаёт заявки только за детей
  const isGuardianOnly = Boolean(user?.roles?.includes(REPRESENTATIVE_ROLE) && !user?.roles?.includes('Спортсмен'));


  const loadWeighInStatuses = useCallback(async () => {
    if (!id) return;
    setLoadingWeighInStatuses(true);
    try {
      const statuses = await weighInProtocolClient.getTournamentStatuses(+id);
      setWeighInStatuses(statuses);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось загрузить статусы протоколов взвешивания');
    } finally {
      setLoadingWeighInStatuses(false);
    }
  }, [id, appContext]);

  const loadCategoryOverviews = useCallback(async (showLoading = true) => {
    if (!id) return;
    if (showLoading) setLoadingOverviews(true);
    try {
      const data = await fetchCategoryOverviews(+id);
      setCategoryOverviews(data);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось загрузить группы турнира');
    } finally {
      if (showLoading) setLoadingOverviews(false);
    }
  }, [id, appContext]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tournamentData = await tournamentClient.getTournament(+id!);
        setTournament(tournamentData);
        setChiefJudgeName(tournamentData.chiefJudgeName ?? '');
        setChiefSecretaryName(tournamentData.chiefSecretaryName ?? '');
        setWeighInProtocolTitle(tournamentData.weighInProtocolTitle ?? '');
        setCompetitionProtocolTitle(tournamentData.competitionProtocolTitle ?? '');

        // Участники и группы доступны только вошедшим пользователям
        if (user) {
          const participantsData = await participantClient.getParticipants(parseInt(id!));
          setParticipants(participantsData);

          const overviewsData = await fetchCategoryOverviews(+id!);
          setCategoryOverviews(overviewsData);
        } else {
          setParticipants([]);
          setCategoryOverviews([]);
        }
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
        setLoadingOverviews(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, user?.id]);

  useEffect(() => {
    if (id && tournament && isAdmin()) {
      loadWeighInStatuses();
    }
  }, [id, tournament?.id, loadWeighInStatuses]);

  const weighInStatusByCategory = useMemo(() => {
    const map = new Map<string, WeighInProtocolStatus>();
    for (const status of weighInStatuses) {
      map.set(`${status.ageCategoryInTournamentId}-${status.weightCategoryInTournamentId}`, status);
    }
    return map;
  }, [weighInStatuses]);

  const activeWeighInAge = activeWeighInStatus
    ? tournament?.ageCategories?.find(c => c.id === activeWeighInStatus.ageCategoryInTournamentId) ?? null
    : null;

  const activeWeighInWeight = activeWeighInStatus
    ? tournament?.weightCategories?.find(c => c.id === activeWeighInStatus.weightCategoryInTournamentId) ?? null
    : null;

  const handleOpenWeighInFromStatus = (status: WeighInProtocolStatus) => {
    setActiveWeighInStatus(status);
    setIsWeighInModalOpen(true);
  };

  const weighInStatusKey = (status: WeighInProtocolStatus) =>
    `${status.ageCategoryInTournamentId}-${status.weightCategoryInTournamentId}`;

  const categoryOverviewKey = (overview: TournamentCategoryOverview) =>
    `${overview.ageCategoryInTournamentId}-${overview.weightCategoryInTournamentId}`;

  const handlePrintWeighInProtocol = async (
    status: WeighInProtocolStatus,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    if (!id) return;

    const key = weighInStatusKey(status);
    setPrintingWeighInKey(key);
    try {
      await weighInProtocolClient.printProtocol(
        +id,
        status.ageCategoryInTournamentId,
        status.weightCategoryInTournamentId
      );
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сформировать PDF протокола');
    } finally {
      setPrintingWeighInKey(null);
    }
  };

  const handleExportAllProtocolsPdf = async () => {
    if (!id) return;

    setExportingAllProtocols(true);
    try {
      await tournamentExportClient.downloadAllProtocolsPdf(+id);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сформировать PDF со всеми протоколами');
    } finally {
      setExportingAllProtocols(false);
    }
  };

  const handlePrintCompetitionProtocol = async (
    overview: TournamentCategoryOverview,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    const bracketId = overview.mainBracketId;
    if (!bracketId) return;

    const key = categoryOverviewKey(overview);
    setPrintingCompetitionKey(key);
    try {
      if (overview.format === 'Mixed') {
        await bracketPrintClient.printMixedProtocol(bracketId);
      } else {
        await bracketPrintClient.printOlympicProtocol(bracketId);
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сформировать PDF протокола');
    } finally {
      setPrintingCompetitionKey(null);
    }
  };

  const handleWeighInDataChanged = async () => {
    try {
      const [refreshed, tournamentData] = await Promise.all([
        participantClient.getParticipants(parseInt(id!)),
        tournamentClient.getTournament(parseInt(id!)),
      ]);
      setParticipants(refreshed);
      setTournament(tournamentData);
      await Promise.all([
        loadCategoryOverviews(false),
        loadWeighInStatuses(),
        loadCategoryDraw(),
      ]);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    }
  };

  const refreshTournament = async () => {
    if (!id) return;
    const tournamentData = await tournamentClient.getTournament(+id);
    setTournament(tournamentData);
  };

  const startTournament = async () => {
    if (!id) return;
    try {
      await tournamentClient.startTournament(+id);
      await refreshTournament();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    }
  };

  const handleFinishTournament = async () => {
    if (!id || !tournament) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Завершить турнир «${tournament.name}»? Он перестанет отображаться как текущий.`)) {
      return;
    }

    setUpdatingStatus(true);
    try {
      await finishTournament(+id);
      await refreshTournament();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось завершить турнир');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReopenTournament = async () => {
    if (!id || !tournament) return;
    setUpdatingStatus(true);
    try {
      await reopenTournament(+id);
      await refreshTournament();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось вернуть турнир в работу');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteTournament = async () => {
    if (!id || !tournament) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(
      `Удалить турнир «${tournament.name}»?\n\nБудут безвозвратно удалены все участники, сетки, поединки и протоколы.`
    )) {
      return;
    }

    setDeletingTournament(true);
    try {
      await tournamentClient.deleteTournament(+id);
      navigate('/tournaments');
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось удалить турнир');
    } finally {
      setDeletingTournament(false);
    }
  };

  const handleOverviewSelect = (overview: TournamentCategoryOverview) => {
    if (!isAdmin()) return; // настройка жеребьёвки — только для администратора
    const age = tournament?.ageCategories?.find(c => c.id === overview.ageCategoryInTournamentId);
    const weight = tournament?.weightCategories?.find(c => c.id === overview.weightCategoryInTournamentId);
    if (age) setSelectedAgeCategoryForBracket(age);
    if (weight) setSelectedWeightCategoryForBracket(weight);
    categoriesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOpenBracket = (bracketId: number) => {
    navigate(`/brackets/${bracketId}`);
  };

  const handlePrintProtocol = async () => {
    const bracketId = selectedCategoryOverview?.mainBracketId;
    if (!bracketId) return;
    setPrintingProtocol(true);
    try {
      const isMixedFormat =
        categoryDraw?.format === 'Mixed'
        || selectedCategoryOverview?.format === 'Mixed';
      if (isMixedFormat) {
        await bracketPrintClient.printMixedProtocol(bracketId);
      } else {
        await bracketPrintClient.printOlympicProtocol(bracketId);
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сформировать PDF протокола');
    } finally {
      setPrintingProtocol(false);
    }
  };

  const loadCategoryDraw = useCallback(async () => {
    if (!tournament?.id || !selectedAgeCategoryForBracket?.id || !selectedWeightCategoryForBracket?.id) {
      setCategoryDraw(null);
      return;
    }
    setLoadingDraw(true);
    try {
      const draw = await fetchCategoryDraw(
        tournament.id,
        selectedWeightCategoryForBracket.id,
        selectedAgeCategoryForBracket.id,
        isFormatOverridden,
        isFormatOverridden ? manualFormat : undefined
      );
      setCategoryDraw(draw);
      if (!isFormatOverridden) {
        setManualFormat(draw.format as CompetitionFormat);
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось загрузить данные категории');
      setCategoryDraw(null);
    } finally {
      setLoadingDraw(false);
    }
  }, [
    tournament?.id,
    selectedAgeCategoryForBracket?.id,
    selectedWeightCategoryForBracket?.id,
    isFormatOverridden,
    manualFormat,
    appContext,
  ]);

  useEffect(() => {
    loadCategoryDraw();
  }, [loadCategoryDraw]);

  const handleGeneratePreview = async () => {
    if (!selectedAgeCategoryForBracket || !selectedWeightCategoryForBracket || !tournament) return;
    setIsGeneratingPreview(true);
    try {
      const preview = await fetchPreviewBracket(
        tournament.id!,
        selectedWeightCategoryForBracket.id!,
        selectedAgeCategoryForBracket.id!,
        isFormatOverridden,
        isFormatOverridden ? manualFormat : undefined
      );
      setBracketPreview(preview);
      setIsPreviewModalOpen(true);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Ошибка генерации сетки');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setBracketPreview(null);
  };

  const handleApproveBracket = async (
    roundsPayload: { roundNumber: number; matches: { homeParticipantId?: number | null; awayParticipantId?: number | null }[] }[]
  ) => {
    if (!tournament || !selectedAgeCategoryForBracket || !selectedWeightCategoryForBracket) return;
    setIsGeneratingPreview(true);
    try {
      await tournamentClient.approveBracket(new ApproveBracketRequest({
        tournamentId: tournament.id,
        ageCategoryInTournamentId: selectedAgeCategoryForBracket.id,
        weightCategoryInTournamentId: selectedWeightCategoryForBracket.id,
        format: bracketPreview?.format || categoryDraw?.format || CompetitionFormat.Olympic,
        isFormatOverridden,
        rounds: roundsPayload.map(rp => new RoundApproveDto({
          roundNumber: rp.roundNumber,
          matches: rp.matches.map(m => new MatchApproveDto({
            homeParticipantId: m.homeParticipantId ?? undefined,
            awayParticipantId: m.awayParticipantId ?? undefined,
          })),
        })),
      }));
      setRebuildingCategoryKey(null);
      await Promise.all([loadCategoryOverviews(false), loadCategoryDraw()]);
      notifySuccess('Сетка успешно утверждена. Карточка группы в списке выше обновлена.');
      overviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      closePreviewModal();
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось утвердить сетку');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleApproveDraw = async (drawPayload: CategoryDrawModel) => {
    if (!selectedAgeCategoryForBracket?.id || !selectedWeightCategoryForBracket?.id) return;
    setIsGeneratingPreview(true);
    try {
      const req = buildApproveRequest(
        drawPayload,
        selectedAgeCategoryForBracket.id,
        selectedWeightCategoryForBracket.id,
        isFormatOverridden,
        drawPayload.format
      );
      await tournamentClient.approveBracket(req);
      setRebuildingCategoryKey(null);
      await Promise.all([loadCategoryOverviews(false), loadCategoryDraw()]);
      notifySuccess('Группа успешно утверждена. Карточка в списке выше обновлена — можно открыть сетку для проведения поединков.');
      overviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось утвердить группу');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleOpenAdminRegisterModal = () => {
    setIsAdminRegisterModalOpen(true);
  };

  const handleCloseAdminRegisterModal = () => {
    setIsAdminRegisterModalOpen(false);
  };

  const handleOpenCreatePersonModal = () => {
    setIsCreatePersonModalOpen(true);
  };

  const handleCloseCreatePersonModal = () => {
    setIsCreatePersonModalOpen(false);
  };

  const handlePersonCreated = () => {
    setIsCreatePersonModalOpen(false);
    setIsAdminRegisterModalOpen(true);
  };

  const handleParticipantAdded = async () => {
    const refreshed = await participantClient.getParticipants(parseInt(id!));
    setParticipants(refreshed);
    await loadCategoryOverviews(false);
  };

  const handleParticipantSaved = (updated: Participant) => {
    setParticipants(prev => prev.map(p => p.id === updated.id ? updated : p));
  };

  const handleDeleteParticipant = async (participantId: number, name: string) => {
    if (!window.confirm(`Удалить участника «${name}» из турнира?`)) return;
    try {
      await participantClient.deleteParticipant(participantId);
      setParticipants(prev => prev.filter(p => p.id !== participantId));
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось удалить участника');
    }
  };

  const selectedCategoryOverview = useMemo(() => {
    if (!selectedAgeCategoryForBracket?.id || !selectedWeightCategoryForBracket?.id) {
      return undefined;
    }
    return categoryOverviews.find(o =>
      o.ageCategoryInTournamentId === selectedAgeCategoryForBracket.id &&
      o.weightCategoryInTournamentId === selectedWeightCategoryForBracket.id
    );
  }, [categoryOverviews, selectedAgeCategoryForBracket?.id, selectedWeightCategoryForBracket?.id]);

  // Пересборка возвращает утверждённую группу в режим настройки: пока в ней не дрались,
  // это единственный способ добавить забытого участника или переиграть жеребьёвку.
  const canRebuildSelectedCategory = !!selectedCategoryOverview?.isApproved
    && !selectedCategoryOverview.hasConductedMatches;

  const isRebuildingSelectedCategory = !!selectedCategoryOverview
    && rebuildingCategoryKey === categoryOverviewKey(selectedCategoryOverview);

  const showSelectedCategoryAsApproved = !!selectedCategoryOverview?.isApproved
    && !isRebuildingSelectedCategory;

  /** Состав уже другой, а сетка ещё старая — нужно пересобрать. */
  const compositionOutOfSync = !!selectedCategoryOverview?.isApproved
    && !selectedCategoryOverview.hasConductedMatches
    && selectedCategoryOverview.totalParticipants !== selectedCategoryOverview.bracketParticipantCount;

  const handleStartRebuild = async () => {
    if (!selectedCategoryOverview || !tournament?.id
      || !selectedAgeCategoryForBracket?.id || !selectedWeightCategoryForBracket?.id) {
      return;
    }

    setRebuildingCategoryKey(categoryOverviewKey(selectedCategoryOverview));
    // Сбрасываем ручной формат: при 5→6 автоматом должна стать смешанная, а не старая круговая.
    setIsFormatOverridden(false);
    setLoadingDraw(true);
    try {
      const draw = await fetchCategoryDraw(
        tournament.id,
        selectedWeightCategoryForBracket.id,
        selectedAgeCategoryForBracket.id,
        false,
        undefined
      );
      setCategoryDraw(draw);
      setManualFormat(draw.format as CompetitionFormat);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось загрузить данные категории');
      setRebuildingCategoryKey(null);
    } finally {
      setLoadingDraw(false);
    }
  };

  const notifySuccess = (message: string) => {
    appContext!.setNotificationData({ message, close: () => {} });
  };

  const handleSaveOfficials = async () => {
    if (!id) return;
    setSavingOfficials(true);
    try {
      await tournamentClient.updateTournamentOfficials(+id, new TournamentOfficialsModel({
        chiefJudgeName: chiefJudgeName.trim() || undefined,
        chiefSecretaryName: chiefSecretaryName.trim() || undefined,
        weighInProtocolTitle: weighInProtocolTitle.trim() || undefined,
        competitionProtocolTitle: competitionProtocolTitle.trim() || undefined,
      }));
      setTournament(prev => prev ? new Tournament({
        ...prev,
        chiefJudgeName: chiefJudgeName.trim() || undefined,
        chiefSecretaryName: chiefSecretaryName.trim() || undefined,
        weighInProtocolTitle: weighInProtocolTitle.trim() || undefined,
        competitionProtocolTitle: competitionProtocolTitle.trim() || undefined,
      }) : prev);
      notifySuccess('Настройки печати сохранены');
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сохранить настройки печати');
    } finally {
      setSavingOfficials(false);
    }
  };

  if (loading) {
    return <div className="tournament-detail">Загрузка...</div>;
  }

  if (!tournament) {
    return <div className="tournament-detail">Турнир не найден</div>;
  }

  const visibleCategoryOverviews = categoryOverviews.filter(
    o => o.totalParticipants > 0 || o.isApproved
  );

  return (
    <div className="tournament-detail">
      <div className="tournament-detail__info">
        <h2>{tournament.name}</h2>
        <p>
          Статус: <strong>{tournamentLifecycleLabel(getTournamentLifecycle(tournament))}</strong>
        </p>
        <p>Дата: {tournament.startDate?.toStringForPreview()}</p>
        <p>Формат определяется отдельно для каждой пары категорий (возраст + вес).</p>
      </div>

      {isAdmin() && (
        <div className="tournament-detail__officials-section">
          <h3>Печатные протоколы</h3>
          <p className="tournament-detail__section-hint">
            ФИО судей и заголовки попадут во все печатные протоколы. Если заголовок пустой — используется стандартный текст.
          </p>
          <div className="tournament-detail__officials-fields">
            <label>
              Главный судья
              <input
                type="text"
                value={chiefJudgeName}
                onChange={(e) => setChiefJudgeName(e.target.value)}
                placeholder="Фамилия Имя Отчество"
                maxLength={150}
              />
            </label>
            <label>
              Главный секретарь
              <input
                type="text"
                value={chiefSecretaryName}
                onChange={(e) => setChiefSecretaryName(e.target.value)}
                placeholder="Фамилия Имя Отчество"
                maxLength={150}
              />
            </label>
          </div>
          <div className="tournament-detail__protocol-titles">
            <label>
              Заголовок протоколов взвешивания
              <input
                type="text"
                value={weighInProtocolTitle}
                onChange={(e) => setWeighInProtocolTitle(e.target.value)}
                placeholder='Например: Протокол взвешивания участников соревнования «Турнир…»'
                maxLength={250}
              />
              <span className="tournament-detail__field-hint">
                Пусто = «Протокол взвешивания участников соревнования «{tournament.name}»»
              </span>
            </label>
            <label>
              Заголовок протоколов хода соревнований
              <input
                type="text"
                value={competitionProtocolTitle}
                onChange={(e) => setCompetitionProtocolTitle(e.target.value)}
                placeholder="Например: ПРОТОКОЛ хода соревнований по дзюдо"
                maxLength={250}
              />
              <span className="tournament-detail__field-hint">
                Пусто = стандартный заголовок («ПРОТОКОЛ» / «ПРОТОКОЛ ХОДА СОРЕВНОВАНИЙ»)
              </span>
            </label>
          </div>
          <button
            type="button"
            className="tournament-detail__action-btn"
            onClick={handleSaveOfficials}
            disabled={savingOfficials}
            style={{ marginTop: 16 }}
          >
            {savingOfficials ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      )}

      <div className="tournament-detail__actions">
        {!isAdmin() && tournament.id != null && isRegistrationOpen(tournament)
          && getTournamentLifecycle(tournament) !== 'finished' && (
          user && participants.some(p => p.personId === user.id) ? (
            <span className="tournament-detail__registered">Вы зарегистрированы на этот турнир</span>
          ) : (
            <button
              onClick={() => (user
                ? navigate(isGuardianOnly
                  ? `/my-children?apply=${tournament.id}`
                  : `/tournaments?apply=${tournament.id}`)
                : navigate('/login', { state: { from: { pathname: '/tournaments', search: `?apply=${tournament.id}` } } }))}
              className="tournament-detail__action-btn tournament-detail__action-btn--new"
            >
              {user ? (isGuardianOnly ? 'Подать заявку за ребёнка' : 'Подать заявку') : 'Войти и подать заявку'}
            </button>
          )
        )}
        {isAdmin() && (
          <>
            <button onClick={() => navigate(`/tournaments/${id}/edit`)} className="tournament-detail__action-btn">Редактировать</button>
            {!tournament.isStarted && getTournamentLifecycle(tournament) !== 'finished' && (
              <button onClick={startTournament} className="tournament-detail__action-btn">Начать турнир</button>
            )}
            {!tournament.isFinished && (
              <button
                onClick={handleFinishTournament}
                className="tournament-detail__action-btn"
                disabled={updatingStatus}
              >
                {updatingStatus ? 'Сохранение…' : 'Завершить турнир'}
              </button>
            )}
            {tournament.isFinished && (
              <button
                onClick={handleReopenTournament}
                className="tournament-detail__action-btn"
                disabled={updatingStatus}
              >
                {updatingStatus ? 'Сохранение…' : 'Вернуть в работу'}
              </button>
            )}
            <button onClick={handleOpenCreatePersonModal} className="tournament-detail__action-btn tournament-detail__action-btn--new">+ Новый участник</button>
            <button onClick={handleOpenAdminRegisterModal} className="tournament-detail__action-btn tournament-detail__action-btn--admin">Регистрация участника (админ)</button>
            <button
              onClick={handleExportAllProtocolsPdf}
              className="tournament-detail__action-btn tournament-detail__action-btn--export"
              disabled={exportingAllProtocols}
            >
              {exportingAllProtocols ? 'Формирование PDF…' : 'Скачать все протоколы (PDF)'}
            </button>
          </>
        )}
        {isOwner() && (
          <button
            onClick={handleDeleteTournament}
            className="tournament-detail__action-btn tournament-detail__action-btn--delete"
            disabled={deletingTournament}
          >
            {deletingTournament ? 'Удаление…' : 'Удалить турнир'}
          </button>
        )}
      </div>

      {isAdmin() &&
        tournament.ageCategories &&
        tournament.ageCategories.length > 0 &&
        tournament.weightCategories &&
        tournament.weightCategories.length > 0 && (
        <div className="tournament-detail__weighin-section">
          <h3>Протокол взвешивания</h3>
          <p className="tournament-detail__section-hint">
            Нажмите на категорию, чтобы открыть протокол и заполнить или изменить его.
            Участников можно добавить прямо при взвешивании — до первого поединка в группе.
          </p>

          <div className="tournament-detail__weighin-status-list">
            {loadingWeighInStatuses ? (
              <p className="tournament-detail__section-hint">Загрузка статусов…</p>
            ) : weighInStatuses.length === 0 ? (
              <p className="tournament-detail__section-hint">Нет категорий для протокола взвешивания.</p>
            ) : (
              <ul className="tournament-detail__weighin-status-grid">
                {weighInStatuses.map((status) => {
                  const statusKey = weighInStatusKey(status);
                  const isPrinting = printingWeighInKey === statusKey;

                  return (
                    <li key={statusKey} className="tournament-detail__weighin-status-row">
                      <button
                        type="button"
                        className={[
                          'tournament-detail__weighin-status-item',
                          status.isCreated
                            ? 'tournament-detail__weighin-status-item--created'
                            : 'tournament-detail__weighin-status-item--missing',
                        ].join(' ')}
                        onClick={() => handleOpenWeighInFromStatus(status)}
                        disabled={isPrinting}
                      >
                        <span className="tournament-detail__weighin-status-label">
                          {status.genderLabel && `${status.genderLabel} · `}
                          {status.ageCategoryLabel} · {status.weightCategoryLabel}
                        </span>
                        <span className="tournament-detail__weighin-status-value">
                          {status.isCreated
                            ? `Протокол создан (${status.entryCount}) →`
                            : 'Протокол не создан →'}
                        </span>
                      </button>
                      {status.isCreated && (
                        <button
                          type="button"
                          className="tournament-detail__weighin-status-print"
                          onClick={(e) => handlePrintWeighInProtocol(status, e)}
                          disabled={isPrinting}
                          title="Печать PDF"
                        >
                          {isPrinting ? '…' : 'PDF'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {!user && (
        <div className="tournament-detail__guest-notice" role="status">
          <h3>Войдите, чтобы посмотреть детали турнира</h3>
          <p>Списки участников, группы и турнирные сетки доступны только зарегистрированным пользователям.</p>
          <div className="tournament-detail__guest-actions">
            <button
              onClick={() => navigate('/login', { state: { from: { pathname: `/tournaments/${tournament.id}` } } })}
              className="tournament-detail__action-btn"
            >
              Войти
            </button>
            <button onClick={() => navigate('/register')} className="tournament-detail__action-btn tournament-detail__action-btn--new">Зарегистрироваться</button>
          </div>
        </div>
      )}

      {user && (tournament.ageCategories && tournament.ageCategories.length > 0 &&
        tournament.weightCategories && tournament.weightCategories.length > 0) && (
        <div className="tournament-detail__section" ref={overviewSectionRef}>
          <h3>Группы турнира</h3>
          <p className="tournament-detail__section-hint">
            {isAdmin()
              ? 'Нажмите на группу, чтобы открыть сетку или таблицу. Для неутверждённых групп откроется настройка жеребьёвки. Утверждённые группы отмечены зелёным бейджем формата.'
              : 'Нажмите на утверждённую группу, чтобы посмотреть сетку или таблицу. Группы без бейджа формата ещё не сформированы.'}
          </p>
          <CategoryOverviewGrid
            overviews={visibleCategoryOverviews}
            loading={loadingOverviews}
            selectedAgeCategoryId={selectedAgeCategoryForBracket?.id}
            selectedWeightCategoryId={selectedWeightCategoryForBracket?.id}
            onSelectCategory={handleOverviewSelect}
            onOpenBracket={handleOpenBracket}
            canConfigure={isAdmin()}
            showWeighInStatus={isAdmin()}
            weighInStatusByCategory={weighInStatusByCategory}
            onPrintWeighIn={handlePrintWeighInProtocol}
            printingWeighInKey={printingWeighInKey}
            onPrintCompetitionProtocol={handlePrintCompetitionProtocol}
            printingCompetitionKey={printingCompetitionKey}
          />
        </div>
      )}

      {isAdmin() && (tournament.ageCategories && tournament.ageCategories.length > 0 &&
        tournament.weightCategories && tournament.weightCategories.length > 0) && (
        <div className="tournament-detail__categories-section" ref={categoriesSectionRef}>
          <h3>{showSelectedCategoryAsApproved ? 'Утверждённая группа' : 'Настройка группы'}</h3>

          {(selectedAgeCategoryForBracket || selectedWeightCategoryForBracket) && (
            <div className="tournament-detail__selection-info">
              <strong>Выбрано:</strong>
              {selectedAgeCategoryForBracket && (
                <span className="tournament-detail__selected-tag">
                  {formatAgeCategoryLabel(selectedAgeCategoryForBracket, tournament.ageCategories ?? [])}
                </span>
              )}
              {selectedWeightCategoryForBracket && (
                <span className="tournament-detail__selected-tag">
                  {formatWeightCategoryLabel(selectedWeightCategoryForBracket, tournament.weightCategories ?? [])}
                </span>
              )}
            </div>
          )}

          {!selectedAgeCategoryForBracket || !selectedWeightCategoryForBracket ? (
            <p className="tournament-detail__section-hint">Выберите группу в списке выше.</p>
          ) : (
            <>
              {loadingDraw ? (
                <p className="category-draw__loading">Загрузка данных категории…</p>
              ) : categoryDraw ? (
                <CategoryDrawPanel
                  draw={categoryDraw}
                  isFormatOverridden={isFormatOverridden}
                  manualFormat={manualFormat}
                  isApproved={showSelectedCategoryAsApproved}
                  canRebuild={canRebuildSelectedCategory}
                  isRebuilding={isRebuildingSelectedCategory}
                  compositionChanged={compositionOutOfSync}
                  bracketParticipantCount={selectedCategoryOverview?.bracketParticipantCount}
                  registeredParticipantCount={selectedCategoryOverview?.totalParticipants}
                  onRebuild={canRebuildSelectedCategory ? handleStartRebuild : undefined}
                  onCancelRebuild={() => setRebuildingCategoryKey(null)}
                  onFormatOverrideChange={setIsFormatOverridden}
                  onManualFormatChange={setManualFormat}
                  onOpenOlympicPreview={handleGeneratePreview}
                  onOpenBracket={
                    selectedCategoryOverview?.mainBracketId
                      ? () => handleOpenBracket(selectedCategoryOverview.mainBracketId!)
                      : undefined
                  }
                  onPrintProtocol={
                    selectedCategoryOverview?.mainBracketId
                    && (categoryDraw.format === CompetitionFormat.Olympic
                      || categoryDraw.format === CompetitionFormat.Mixed)
                      ? handlePrintProtocol
                      : undefined
                  }
                  printingProtocol={printingProtocol}
                  onApprove={
                    !showSelectedCategoryAsApproved
                    && (
                      categoryDraw.format === CompetitionFormat.RoundRobin
                      || categoryDraw.format === CompetitionFormat.Mixed
                    )
                      ? handleApproveDraw
                      : undefined
                  }
                  approving={isGeneratingPreview}
                  loadingPreview={isGeneratingPreview}
                />
              ) : null}
            </>
          )}
        </div>
      )}

      {user && (
      <div className="tournament-detail__section">
        <h3>Участники ({participants.length})</h3>
        {participants.length === 0 ? (
          <p>Нет зарегистрированных участников</p>
        ) : (
          <div className="tournament-detail__participant-list">
            <table>
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Вес</th>
                  <th>Спортивная школа</th>
                  <th>Ранг</th>
                  <th>Тренер</th>
                  {isAdmin() && <th style={{ width: 160 }}></th>}
                </tr>
              </thead>
              <tbody>
                {participants.map((participant) => {
                  // Точная дата рождения приходит только админу и самому спортсмену; остальным — год рождения
                  const birth = participant.person?.birthDate ? new Date(participant.person.birthDate) : null;
                  const today = new Date();
                  const age = birth && !Number.isNaN(birth.getTime())
                    ? today.getFullYear() - birth.getFullYear()
                      - (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0)
                    : null;
                  const birthYear = participant.person?.birthYear;
                  const fullName = participant.person
                    ? `${participant.person.lastName} ${participant.person.firstName} ${participant.person.patronymic || ''}`.trim()
                    : 'Unknown';
                  const ageLabel = isAdmin() && age !== null
                    ? `${age} лет`
                    : birthYear ? `${birthYear} г.р.` : null;
                  return (
                    <tr key={participant.id}>
                      <td>{ageLabel ? `${fullName} (${ageLabel})` : fullName}</td>
                      <td>{participant.weight} кг</td>
                      <td>{participant.organization}</td>
                      <td>{participant.rank || 'N/A'}</td>
                      <td>
                        {participant.coach
                          ? formatShortName(participant.coach)
                          : 'N/A'}
                      </td>
                      {isAdmin() && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            className="btn btn--edit"
                            style={{ marginRight: 6 }}
                            onClick={() => setEditingParticipant(participant)}
                          >
                            Изменить
                          </button>
                          <button
                            className="btn btn--delete"
                            onClick={() => handleDeleteParticipant(participant.id!, fullName)}
                          >
                            Удалить
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {isAdmin() && (
          <div className="tournament-detail__section-actions">
            <button
              onClick={handleOpenAdminRegisterModal}
              className="tournament-detail__action-btn tournament-detail__action-btn--admin"
            >
              Зарегистрировать участника (для админа)
            </button>
          </div>
        )}
      </div>
      )}

      {isPreviewModalOpen && bracketPreview && (
        <div
          style={{
            position: 'fixed', inset: 0,
            zIndex: 1000, display: 'flex', alignItems: 'flex-start',
            justifyContent: 'center', overflowY: 'auto', padding: '40px 16px',
          }}
          onClick={closePreviewModal}
        >
          <div
            style={{
              background: 'white', borderRadius: 12, padding: '1.5rem',
              maxWidth: 1300, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0 }}>Предварительная сетка</h3>
                <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
                  {bracketPreview.weightCategory} · {bracketPreview.ageCategory}
                </p>
              </div>
              <button className="btn btn--back" onClick={closePreviewModal}>✕ Закрыть</button>
            </div>
            <BracketsPreview
              data={bracketPreview}
              onApprove={isGeneratingPreview ? undefined : handleApproveBracket}
            />
            {isGeneratingPreview && (
              <p style={{ textAlign: 'center', color: '#6b7280', marginTop: '1rem' }}>Утверждение сетки...</p>
            )}
          </div>
        </div>
      )}

      {tournament && (
        <AdminParticipantModal
          isOpen={isAdminRegisterModalOpen}
          onClose={handleCloseAdminRegisterModal}
          tournament={tournament}
          onParticipantAdded={handleParticipantAdded}
        />
      )}

      <CreatePersonModal
        isOpen={isCreatePersonModalOpen}
        onClose={handleCloseCreatePersonModal}
        onPersonCreated={handlePersonCreated}
      />

      {editingParticipant && tournament && (
        <EditParticipantModal
          isOpen={!!editingParticipant}
          onClose={() => setEditingParticipant(null)}
          participant={editingParticipant}
          tournament={tournament}
          onSaved={handleParticipantSaved}
        />
      )}

      {isWeighInModalOpen && tournament && activeWeighInAge && activeWeighInWeight && (
        <WeighInModal
          isOpen={isWeighInModalOpen}
          onClose={() => {
            setIsWeighInModalOpen(false);
            setActiveWeighInStatus(null);
            loadWeighInStatuses();
          }}
          tournament={tournament}
          ageCategory={activeWeighInAge}
          weightCategory={activeWeighInWeight}
          onParticipantsUpdated={handleWeighInDataChanged}
          onProtocolChanged={loadWeighInStatuses}
        />
      )}
    </div>
  );
};
